import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import Button from 'common/Button';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import renderGroups from 'utils/renderGroups';
import pageActions from 'core/page/actions';
import { withStyles } from 'theme/withStyles';
import ErrorMsg from 'common/ErrorMsg';

import rootStyles from './styles/root';

const styles = (theme) => {
  const common = {
    root: rootStyles(theme),
  };
  return {
    ...common,
    back: {
      marginTop: theme.spacing.xs,
    },
    buttonRow: {
      display: 'block',
      margin: '0 auto',
      maxWidth: theme.forms.maxWidth,
      padding: [0, theme.spacing.sm],

      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        display: 'block',
      },
    },
    buttonRowWrapper: {
      display: 'flex',
      flexDirection: 'column-reverse',
      margin: [theme.spacing.xl, 0, 0, 0],
    },
    root: {
      '& .btn-row-group': {
        display: 'none',
        [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
          display: 'none',
        },
      },
      textAlign: 'center',
    },
  };
};

export const DoneBare = ({
  classes,
  continueAllowed,
  continueJoint,
  disableButton,
  groups,
  headerHtml,
  inviteJoint,
  isMessageDisclosureEnabled,
  logout,
  name,
  onNext,
  resources,
  saveAndContinueAllowed,
  saveButtonLabel,
}) => (
  <div className={classnames(`done-view ${name}`, classes.root)}>
    <RawHtml>{headerHtml}</RawHtml>

    <ErrorMsg type="page" />

    <div className="row">
      <div className="widget">
        <div className="region-groups">{renderGroups(groups)}</div>
      </div>
    </div>

    <div className={classnames('row btn-row-page', `btn-page-${name}`, classes.buttonRow)}>
      {resources.continueJointLaterButtonLabel || resources.continueAsJointButtonLabel ? (
        <div className={classnames('col-lg-12', classes.buttonRowWrapper)}>
          <RawHtml
            className="btn btn-mobile btn-tertiary mt-5 btn-page-later"
            data-name="btn-page-later"
            type="button"
            onClick={inviteJoint}
            disabled={disableButton}
            variant="secondary"
          >
            {resources.continueJointLaterButtonLabel}
          </RawHtml>
          {saveAndContinueAllowed && (
            <RawHtml
              className="btn btn-mobile btn-tertiary btn-save-and-continue"
              data-name="btn-save-and-continue"
              type="button"
              disabled={disableButton}
              variant="secondary"
            >
              {saveButtonLabel}
            </RawHtml>
          )}
          {resources.continueAsJointButtonLabel && resources.continueAsJointButtonLabel.length > 0 && (
            <Button
              className="btn btn-mobile mt-5 btn-page-now"
              data-name="btn-page-now"
              disabled={disableButton}
              onClick={continueJoint}
            >
              <RawHtml type="span">{resources.continueAsJointButtonLabel}</RawHtml>
              <i className="icon-arrow-thin-right" aria-hidden="true" />
              <Spinner loading={false} />
            </Button>
          )}
        </div>
      ) : (
        <div className={classnames('col-lg-12', classes.buttonRowWrapper)}>
          {resources.logoutButtonLabel && (
            <RawHtml
              className={classnames('btn btn-mobile btn-tertiary btn-page-back mt-5', classes.back)}
              data-name="btn-page-back"
              type="button"
              disabled={disableButton}
              onClick={() => logout(resources.logoutRedirectUrl)}
              variant="secondary"
            >
              {resources.logoutButtonLabel}
            </RawHtml>
          )}
          {saveAndContinueAllowed && (
            <RawHtml
              className="btn btn-mobile btn-tertiary btn-save-and-continue"
              data-name="btn-save-and-continue"
              type="button"
              disabled={disableButton}
              variant="secondary"
            >
              {saveButtonLabel}
            </RawHtml>
          )}
          {!isMessageDisclosureEnabled && continueAllowed && (
            <Button
              className={`btn btn-mobile btn-page-next btn-next-${name}`}
              data-name="btn-page-next"
              disabled={disableButton}
              onClick={onNext}
            >
              <RawHtml type="span">{resources.homeButtonLabel}</RawHtml>
              <i className="icon-arrow-thin-right" aria-hidden="true" />
              <Spinner loading={false} />
            </Button>
          )}
        </div>
      )}
    </div>
  </div>
);

DoneBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  continueAllowed: PropTypes.bool,
  continueJoint: PropTypes.func.isRequired,
  disableButton: PropTypes.bool,
  groups: PropTypes.shape({}),
  headerHtml: PropTypes.string,
  inviteJoint: PropTypes.func.isRequired,
  isMessageDisclosureEnabled: PropTypes.bool,
  logout: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
  resources: PropTypes.shape({
    continueAsJointButtonLabel: PropTypes.string,
    continueJointLaterButtonLabel: PropTypes.string,
    homeButtonLabel: PropTypes.string,
    logoutButtonLabel: PropTypes.string,
    logoutRedirectUrl: PropTypes.string,
  }),
  saveAndContinueAllowed: PropTypes.bool,
  saveButtonLabel: PropTypes.string,
};

DoneBare.defaultProps = {
  continueAllowed: true,
  disableButton: false,
  groups: {},
  headerHtml: '',
  isMessageDisclosureEnabled: false,
  resources: {},
  saveAndContinueAllowed: false,
  saveButtonLabel: null,
};

const mapStateToProps = ({ configs, groups, page, resources }) => ({
  backAllowed: page.backAllowed,
  continueAllowed: page.continueAllowed,
  errorMsg: page.errorMsg,
  groups,
  headerHtml: page.headerHtml,
  isMessageDisclosureEnabled: configs?.isMessageDisclosureEnabled,
  name: page.name,
  resources,
  saveAndContinueAllowed: page.saveAndContinueAllowed,
  saveButtonLabel: page.saveButtonLabel,
});

const mapDispatchToProps = {
  continueJoint: pageActions.continueJoint,
  inviteJoint: pageActions.inviteJoint,
  logout: pageActions.logout,
  onNext: () => pageActions.goToNonWorkflowPage('home'),
};

const DoneStyled = withStyles(styles)(DoneBare);

export default connect(mapStateToProps, mapDispatchToProps)(DoneStyled);
